import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import {
  GameStats,
  H2HStats,
  Match,
  MatchLogsProps,
  MatchPropsShortModalRes,
  MatchPropsShortRes,
} from '@/modules/results/types';
import { DefaultMiddlewareRes } from '@/utils/helpers/middlewareTypes';

const ITEMS_PER_PAGE = 25;

async function getResultMatchInfos(id: string): Promise<{
  data: Match[];
  error?: string;
  /** A real 404, as opposed to a transient failure — the caller renders notFound() only for this. */
  isNotFound?: boolean;
}> {
  let results: Match[] = [];

  try {
    const response = await fetch(`/api/v1/results/getResultMatchInfos?id=${id}`);

    if (response.status === 404) {
      return { data: [], isNotFound: true };
    }

    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }

    const data: DefaultMiddlewareRes<Match[]> = await response.json();
    results = data.data || [];
    return { data: results };
  } catch {
    return { data: [], error: 'Failed to fetch match information!' };
  }
}

export const useGetResultMatchInfos = (id: string, enableRefetch?: boolean) => {
  return useQuery({
    queryKey: ['match_infos', id],
    queryFn: () => getResultMatchInfos(id),
    enabled: id !== '',
    refetchOnWindowFocus: false,
    refetchInterval: enableRefetch ? 15000 : undefined,
  });
};

async function getMatchesStats(id: string): Promise<{
  data: GameStats | undefined;
  error?: string;
}> {
  try {
    const response = await fetch(`/api/v1/results/getMatchesStats?id=${id}`);

    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }

    const data: DefaultMiddlewareRes<GameStats> = await response.json();
    return { data: data.data };
  } catch {
    return { data: undefined, error: 'Failed to fetch match stats!' };
  }
}

export const useGetMatchesStats = (id: string, enableRefetch?: boolean) => {
  return useQuery({
    queryKey: ['matches_stats', id],
    queryFn: () => getMatchesStats(id),
    enabled: id !== '',
    refetchOnWindowFocus: false,
    refetchInterval: enableRefetch ? 15000 : undefined,
  });
};

async function getMatchesH2H(id: string): Promise<{ data: H2HStats | undefined; error?: string }> {
  try {
    const response = await fetch(`/api/v1/results/getMatchesH2H?id=${id}`);

    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }

    const data: DefaultMiddlewareRes<H2HStats> = await response.json();
    return { data: data.data };
  } catch {
    return { data: undefined, error: 'Failed to fetch match head to head!' };
  }
}

export const useGetMatchesH2H = (id: string) => {
  return useQuery({
    queryKey: ['matches_H2H', id],
    queryFn: () => getMatchesH2H(id),
    enabled: id !== '',
    refetchOnWindowFocus: false,
  });
};

async function getListLogs(id: string): Promise<{
  data: MatchLogsProps[];
  error?: string;
}> {
  let results: MatchLogsProps[] = [];

  try {
    const response = await fetch(`/api/v1/results/getListLogs?id=${id}`);

    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }

    const data: DefaultMiddlewareRes<MatchLogsProps[]> = await response.json();
    results = data.data || [];
    return { data: results };
  } catch {
    return { data: [], error: 'Failed to fetch logs data!' };
  }
}

export const useGetListLogs = (id: string, enableRefetch?: boolean) => {
  return useQuery({
    queryKey: ['matches_list_logs', id],
    queryFn: () => getListLogs(id),
    enabled: id !== '',
    refetchOnWindowFocus: false,
    refetchInterval: enableRefetch ? 15000 : undefined,
  });
};

async function getPlayerMatches(
  idOne: string,
  idTwo: string = '',
  type: string,
  page: number = 1,
  formatTitle: string,
  date: string
): Promise<MatchPropsShortModalRes> {
  const urlParams = new URLSearchParams({
    idOne,
    idTwo,
    type,
    page: page.toString(),
    formatTitle,
    date,
  }).toString();

  try {
    const response = await fetch(`/api/v1/results/getPlayerMatches?${urlParams}`);

    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }

    const data: DefaultMiddlewareRes<MatchPropsShortRes> = await response.json();
    return {
      results: data.data.results || [],
      totalRecords: data.data.total_records,
    };
  } catch {
    return { results: [], totalRecords: 0, error: 'Failed to fetch player matches!' };
  }
}

export const useGetPlayerMatches = (
  idOne: string,
  idTwo: string,
  type: string,
  formatTitle: string,
  date: string
) => {
  return useInfiniteQuery({
    queryKey: ['player_matches', idOne, idTwo, type, formatTitle],
    queryFn: async ({ pageParam = 1 }) =>
      getPlayerMatches(idOne, idTwo, type, pageParam, formatTitle, date),

    enabled: idOne !== '' && formatTitle !== undefined,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    getNextPageParam: (lastPage, allPages) => {
      const totalRecords = lastPage.totalRecords;
      const itemsPerPage = ITEMS_PER_PAGE;
      const totalPages = Math.ceil(totalRecords / itemsPerPage);
      const nextPage = allPages.length + 1;

      return nextPage <= totalPages ? nextPage : undefined;
    },
  });
};

export const useGetPlayerModalMatches = (
  idOne: string,
  idTwo: string,
  type: string,
  formatTitle: string,
  date: string,
  isOpen: boolean,
  isSuperAdmin: boolean,
  shouldFetch: boolean
) => {
  return useInfiniteQuery({
    queryKey: ['player_matches_modal', idOne, idTwo, type, formatTitle, date],
    queryFn: async ({ pageParam = 1 }) =>
      getPlayerMatches(idOne, idTwo, type, pageParam, formatTitle, date),

    enabled: !!idOne && formatTitle !== undefined && isOpen && isSuperAdmin && shouldFetch,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    getNextPageParam: (lastPage, allPages) => {
      const totalRecords = lastPage.totalRecords;
      const itemsPerPage = ITEMS_PER_PAGE;
      const totalPages = Math.ceil(totalRecords / itemsPerPage);
      const nextPage = allPages.length + 1;

      return nextPage <= totalPages ? nextPage : undefined;
    },
  });
};

async function getPrevMatches(ids: string[], page: number = 1): Promise<MatchPropsShortModalRes> {
  const uuidsString = ids.join(',');
  try {
    const response = await fetch(`/api/v1/results/getPrevMatches?ids=${uuidsString}&page=${page}`);

    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }

    const data: DefaultMiddlewareRes<MatchPropsShortRes> = await response.json();
    return { results: data.data.results || [], totalRecords: data.data.total_records };
  } catch {
    return { results: [], totalRecords: 0, error: 'Failed to fetch previous matches!' };
  }
}

export const useGetPrevMatches = (ids: string[]) => {
  return useInfiniteQuery({
    queryKey: ['prev_matches', ids],
    queryFn: async ({ pageParam = 1 }) => getPrevMatches(ids, pageParam),
    enabled: ids.length > 0,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    getNextPageParam: (lastPage, allPages) => {
      const totalRecords = lastPage.totalRecords;
      const itemsPerPage = ITEMS_PER_PAGE;
      const totalPages = Math.ceil(totalRecords / itemsPerPage);
      const nextPage = allPages.length + 1;

      return nextPage <= totalPages ? nextPage : undefined;
    },
  });
};

export const useGetPrevModalMatches = (
  ids: string[],
  isOpen: boolean,
  shouldUsePrevMatches: boolean,
  isSuperAdmin: boolean
) => {
  return useInfiniteQuery({
    queryKey: ['prev_matches_modal', ids],
    queryFn: async ({ pageParam = 1 }) => getPrevMatches(ids, pageParam),
    enabled: ids.length > 0 && isOpen && shouldUsePrevMatches && isSuperAdmin,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    getNextPageParam: (lastPage, allPages) => {
      const totalRecords = lastPage.totalRecords;
      const itemsPerPage = ITEMS_PER_PAGE;
      const totalPages = Math.ceil(totalRecords / itemsPerPage);
      const nextPage = allPages.length + 1;

      return nextPage <= totalPages ? nextPage : undefined;
    },
  });
};
